import React from 'react';
import { Redirect, useLocation, useHistory } from 'react-router';
import queryString from 'query-string'
import { Formik, Form, FormikValues } from 'formik';
import { Grid, Typography, Button } from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import LockIcon from '@material-ui/icons/Lock';
import CheckIcon from '@material-ui/icons/Check';

import { FullScreenCard, Loading, Spacing, TextInput, MessageCard } from 'components';
import { LINKS } from 'constants/index';
import useGlobalStyles from 'hooks/useGlobalStyles';
import useAuth from 'hooks/useAuth';

import * as schemas from './components/ValidationSchema';

const MODES = {
  resetPassword: 'Password',
  recoverEmail: 'Email',
  forgotPassword: 'Forgot Password'
};

const LABELS = {
  resetPassword: 'New Password',
  recoverEmail: 'New Email',
  forgotPassword: 'Email'
};

const MESSAGES = {
  error: (msg: string) => `Sorry, ${msg || 'something\'s gone wrong'}. Please try again. If the problem persists, please contact support.`,
  sentEmail: `An email has been sent to the provided email. To securely update your account, click the link in your inbox.`
}

const ResetCredentials: React.SFC = () => {
  // Local State
  const globalClasses = useGlobalStyles();
  const { error, isLoading, resetPassword, sendPasswordReset } = useAuth();
  const { search } = useLocation();
  const history = useHistory();
  const { mode: paramsMode, oobCode } = queryString.parse(search);

  // @ts-ignore
  const mode = MODES[paramsMode];
  // @ts-ignore
  const label = LABELS[paramsMode];
  const schemaName = `${paramsMode}Schema`;
  // @ts-ignore
  const validationSchema = schemas[schemaName];
  const icon = (mode === MODES.resetPassword) ?  <LockIcon /> : <AccountCircleIcon />;
  const inputType = (mode === MODES.resetPassword) ? 'password' : 'email';
  const placeholder = (mode === MODES.resetPassword) ? '********' : 'person@example.com';
  const title = (mode === MODES.forgotPassword) ? mode : `Reset Your ${mode}`;
  const initialValues = { input: '' };
  const initialErrors = { input: '' };
  const [showMessage, setShowMessage] = React.useState(false);
  
  const handleSubmit = async (values: FormikValues) => {
    const { input } = values;

    if (mode === MODES.resetPassword) {
      await resetPassword(oobCode as string, input);
    } else if (mode === MODES.forgotPassword) {
      await sendPasswordReset(input);
    }
    setShowMessage(true);
  }
    
  React.useEffect(() => {
    if(showMessage && error === '' && mode === MODES.resetPassword) {
      history.push('/signin');
    }
    return undefined;
  }, [showMessage, error, mode, history]);

  const renderForm = () => (
    <Formik
      initialValues={initialValues}
      initialErrors={initialErrors}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      { ({ isValid, values, handleChange, handleBlur, touched, errors }) => (
        <Form>
          <Grid className={globalClasses.gridNoSpacing} container spacing={0} direction="column">
            <Grid item>
              <TextInput
                title={label}
                name="input"
                placeholder={placeholder}
                value={values.input}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={touched.input && errors.input}
                endAdornment={icon}
                type={inputType}
              />
            </Grid>
            <Grid item>
              <Grid container>
                <Grid item xs={6}>
                  <Button variant="outlined" href="/signin">
                    Sign In
                  </Button>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'right' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid || isLoading}
                  >
                    {isLoading && (
                      <Loading size={20} style={{ marginTop: '-10px' }} />
                    )}
                    {!isLoading && 'Submit'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  if (!mode) {
    return <Redirect to="/" />;
  }

  const renderMessage = () => showMessage && (
    <MessageCard 
      icon={error === '' ? <CheckIcon /> : <WarningRoundedIcon />}
      body={error === '' ? MESSAGES.sentEmail : MESSAGES.error(error)}
      actions={[
        { label: 'Contact Support', href: LINKS.HELP },
        { label: 'Retry', href: '/signin' }
      ]}
    />
  );

  return (
    <FullScreenCard size="mini" footer={renderMessage()}>
      <Grid
        className={globalClasses.gridNoSpacing}
        style={{ height: '100%' }}
        container
        justify="center"
        spacing={0}
        direction="column"
      >
        <Grid item>
          <Typography align="center" variant="h2">
            {title}
          </Typography>
        </Grid>
        <Spacing />
        <Grid item>
          {renderForm()}
        </Grid>
      </Grid>
    </FullScreenCard>
  );
}

export default ResetCredentials;
